<template>
  <div
    :class="[
      'badiu-tiptap-editor',
      fullscreen ? 'badiu-tiptap-fullscreen' : ''
    ]"
  >
    <!-- Isolated toolbar -->
    <badiu-tiptap-toolbar
      :editor="editor"
      :fullscreen="fullscreen"
      @open-link="openLinkModal"
      @open-image="openImageModal"
      @open-source="openSourceModal"
      @insert-table="insertTable"
      @toggle-fullscreen="toggleFullscreen"
    />

    <!-- Editable content area -->
    <editor-content
      :editor="editor"
      class="badiu-tiptap-content"
    />

    <!-- Status bar (footer) — TinyMCE-style -->
    <div
      v-if="editor"
      class="badiu-tiptap-statusbar"
    >
      <span
        v-if="showCounter && editor.storage.characterCount"
        class="badiu-tiptap-counter"
      >
        {{ editor.storage.characterCount.characters() }} characters
        ·
        {{ editor.storage.characterCount.words() }} words
      </span>

      <span class="badiu-tiptap-brand">
        Badiu Editor
      </span>
    </div>

    <!-- Image modal (isolated component) -->
    <badiu-tiptap-image
      :show="showImageModal"
      :initial="imageInitial"
      :config="config"
      @save="saveImage"
      @cancel="closeImageModal"
      @uploaded="onImageUploaded"
      @error="onUploadError"
    />

    <!-- Link modal (isolated component) -->
    <badiu-tiptap-link
      :show="showLinkModal"
      :initial="linkInitial"
      :config="config"
      @save="saveLink"
      @remove="removeLink"
      @cancel="closeLinkModal"
      @uploaded="onLinkUploaded"
      @error="onUploadError"
    />

    <!-- Source code modal (isolated component) -->
    <badiu-tiptap-source-code
      :show="showSourceModal"
      :html="sourceHtml"
      @save="saveSource"
      @cancel="closeSourceModal"
    />
  </div>
</template>

<script>
/**
 * BadiuTiptapEditor
 *
 * TinyMCE-inspired HTML editor built on Tiptap:
 *  - Toolbar grouped into logical sections (BadiuTiptapToolbar)
 *  - Separate modal for each feature:
 *     · BadiuTiptapImage       → image modal (form + preview)
 *     · BadiuTiptapImageUpload → isolated image upload
 *     · BadiuTiptapFileUpload  → generic file upload (used by link)
 *     · BadiuTiptapLink        → link modal (URL / attachment / target)
 *     · BadiuTiptapSourceCode  → HTML source view / edit modal
 *  - Fullscreen mode
 *  - Paste images as base64
 */
import { ref, reactive, computed, watch, onBeforeUnmount } from 'vue'
import { Editor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import CharacterCount from '@tiptap/extension-character-count'

import BadiuTiptapToolbar from './BadiuTiptapToolbar.vue'
import BadiuTiptapImage from './BadiuTiptapImage.vue'
import BadiuTiptapLink from './BadiuTiptapLink.vue'
import BadiuTiptapSourceCode from './BadiuTiptapSourceCode.vue'

/* ============================================================
 * PreserveAttributes — preserva atributos HTML arbitrários
 * (class, style, id) em paragraph, heading, lists, blockquote,
 * tabela, link, image. Sem isto, Tiptap descarta tudo que não
 * está declarado no schema, removendo class="..." Bootstrap
 * etc. quando o usuário edita via "view source code".
 *
 * Implementado via TextAlign.extend() para reusar a base
 * Extension sem adicionar dependência em @tiptap/core
 * (mesmo padrão de FontFamily/FontSize acima).
 * ============================================================ */
const PreserveAttributes = TextAlign.extend({
  name: 'preserveAttributes',

  addOptions() {
    return {
      types: [
        'paragraph',
        'heading',
        'blockquote',
        'bulletList',
        'orderedList',
        'listItem',
        'codeBlock',
        'table',
        'tableRow',
        'tableHeader',
        'tableCell',
        'image',
        'link'
      ]
    };
  },

  addGlobalAttributes() {
    /* Atributos arbitrários preservados via uma chave própria
     * ("customClass", "customStyle", "customId") para não
     * colidir com renderHTML de outras extensions (ex.: TextAlign
     * também escreve em style). Quando o Tiptap mescla, a última
     * chave igual no objeto vence — usar chaves diferentes evita
     * o conflito e o ProseMirror combina via mergeAttributes.
     */
    return [
      {
        types: this.options.types,
        attributes: {
          customClass: {
            default: null,
            parseHTML: (el) => el.getAttribute('class') || null,
            renderHTML: (attributes) => {
              if (!attributes.customClass) return {};
              return { class: attributes.customClass };
            }
          },
          customStyle: {
            default: null,
            parseHTML: (el) => el.getAttribute('style') || null,
            renderHTML: (attributes) => {
              if (!attributes.customStyle) return {};
              return { style: attributes.customStyle };
            }
          },
          customId: {
            default: null,
            parseHTML: (el) => el.getAttribute('id') || null,
            renderHTML: (attributes) => {
              if (!attributes.customId) return {};
              return { id: attributes.customId };
            }
          }
        }
      }
    ];
  },

  addKeyboardShortcuts() { return {}; }
});

/* ============================================================
 * TextStyle — inline Mark definition.
 * Wraps content in <span style="..."> so that font-family and
 * font-size attributes have a Mark to attach to.
 * Not included in the vendor bundle; built via Underline.extend().
 * ============================================================ */
const TextStyle = Underline.extend({
  name: "textStyle",
  priority: 101,

  addOptions() {
    return { HTMLAttributes: {} };
  },

  parseHTML() {
    return [{ tag: "span", getAttrs: (el) => (el.hasAttribute("style") ? {} : false) }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes, 0];
  },

  addCommands() {
    return {
      removeEmptyTextStyle: () => ({ commands }) => commands.unsetMark(this.name)
    };
  },

  addKeyboardShortcuts() { return {}; },
  addInputRules()        { return []; },
  addPasteRules()        { return []; }
});

/* ============================================================
 * FontFamily — inline Extension definition.
 * Adds a fontFamily attribute to the textStyle mark and exposes
 * setFontFamily / unsetFontFamily commands.
 * Built via TextAlign.extend() to reuse the Extension base class.
 * ============================================================ */
const FontFamily = TextAlign.extend({
  name: "fontFamily",

  addOptions() {
    return { types: ["textStyle"] };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontFamily: {
            default: null,
            parseHTML: (el) => el.style.fontFamily?.replace(/['"]+/g, "") || null,
            renderHTML: (attrs) =>
              attrs.fontFamily ? { style: `font-family: ${attrs.fontFamily}` } : {}
          }
        }
      }
    ];
  },

  addCommands() {
    return {
      setFontFamily: (fontFamily) => ({ chain }) =>
        chain().setMark("textStyle", { fontFamily }).run(),
      unsetFontFamily: () => ({ chain }) =>
        chain().setMark("textStyle", { fontFamily: null }).run()
    };
  },

  addKeyboardShortcuts() { return {}; }
});

/* ============================================================
 * FontSize — inline Extension definition.
 * Adds a fontSize attribute to the textStyle mark and exposes
 * setFontSize / unsetFontSize commands.
 * Built via TextAlign.extend() to reuse the Extension base class.
 * ============================================================ */
const FontSize = TextAlign.extend({
  name: "fontSize",

  addOptions() {
    return { types: ["textStyle"] };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (el) => el.style.fontSize || null,
            renderHTML: (attrs) =>
              attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {}
          }
        }
      }
    ];
  },

  addCommands() {
    return {
      setFontSize: (fontSize) => ({ chain }) =>
        chain().setMark("textStyle", { fontSize }).run(),
      unsetFontSize: () => ({ chain }) =>
        chain().setMark("textStyle", { fontSize: null }).run()
    };
  },

  addKeyboardShortcuts() { return {}; }
});

export default {
  name: "BadiuTiptapEditor",

  components: {
    EditorContent,
    BadiuTiptapToolbar,
    BadiuTiptapImage,
    BadiuTiptapLink,
    BadiuTiptapSourceCode
  },

  props: {
    modelValue: {
      type: String,
      default: ""
    },

    placeholder: {
      type: String,
      default: "Type your content..."
    },

    showCounter: {
      type: Boolean,
      default: true
    },

    /**
     * Runtime configuration passada como objeto simples.
     *
     * Chaves suportadas:
     *   upload_baseurl       URL base do servidor Badiu para uploads
     *                        (ex.: "https://host/path/public").
     *   external_api_vendor  (opcional) Objeto vendor do host que expõe
     *                        Vue e axios (ex.: window.BADIUNET_SYSTEM_CORE_VENDOR).
     *                        Quando fornecido, axios é obtido dele; caso
     *                        contrário usa window.BADIUNET_SYSTEM_CORE_VENDOR
     *                        como fallback ou fica undefined (sem upload).
     *
     * Exemplo:
     *   :config="{ upload_baseurl: 'https://...', external_api_vendor: window.BADIUNET_SYSTEM_CORE_VENDOR }"
     */
    config: {
      type: Object,
      default: () => ({})
    }
  },

  emits: [
    "update:modelValue",
    "image-uploaded",
    "file-uploaded",
    "upload-error"
  ],

  setup(props, { emit }) {
    /* ============================================================
     * Base URL — getter / setter
     *
     * Priority order:
     *   1. Value set programmatically via o setter (_baseUrlOverride)
     *   2. config.upload_baseurl fornecido via prop
     *   3. Default embutido
     * ============================================================ */
    const _baseUrlOverride = ref(null);

    const baseUrl = computed({
      get() {
        return (
          _baseUrlOverride.value ??
          props.config?.upload_baseurl ??
          "https://ds4.badiu21.com.br/badiunet/geral/public"
        );
      },
      set(value) {
        _baseUrlOverride.value = value;
      }
    });

    /* ============================================================
     * Modal state (each modal is its own isolated component)
     * ============================================================ */
    const showImageModal  = ref(false);
    const showLinkModal   = ref(false);
    const showSourceModal = ref(false);

    const imageInitial = reactive({});
    const linkInitial  = reactive({});
    const sourceHtml   = ref("");

    /* ============================================================
     * Fullscreen state
     * ============================================================ */
    const fullscreen = ref(false);

    /* ============================================================
     * Tiptap editor instance
     * ============================================================ */
    const extensions = [
      StarterKit,

      Underline,
      TextStyle,
      FontFamily,
      FontSize,

      Image.configure({
        inline: false,
        allowBase64: true
      }),

      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: { rel: null, target: null }
      }),

      Placeholder.configure({
        placeholder: props.placeholder
      }),

      TextAlign.configure({
        types: ["heading", "paragraph"]
      }),

      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,

      TaskList,

      TaskItem.configure({ nested: true }),

      CharacterCount,

      PreserveAttributes
    ].filter(Boolean);

    const editor = new Editor({
      content: props.modelValue,
      extensions,
      editorProps: {
        handleDoubleClick(view, pos, event) {
          if (event.target && event.target.nodeName === "IMG") {
            openImageModal();
            return true;
          }
          return false;
        },

        /**
         * Handles image paste from the clipboard (screenshots, crops, etc.).
         * Images are inserted as base64 directly — no upload required.
         *
         * HTML images already present in the clipboard (with <img src>)
         * are handled natively by Tiptap via Image.configure({ allowBase64: true }).
         */
        handlePaste(view, event) {
          const clipboard = event.clipboardData;

          if (!clipboard || !clipboard.items) return false;

          for (let i = 0; i < clipboard.items.length; i++) {
            const item = clipboard.items[i];

            if (item.kind === "file" && item.type.startsWith("image/")) {
              const file = item.getAsFile();

              if (!file) continue;

              const reader = new FileReader();

              reader.onload = (e) => {
                const base64 = e.target.result;

                editor
                  .chain()
                  .focus()
                  .setImage({ src: base64 })
                  .run();
              };

              reader.readAsDataURL(file);

              event.preventDefault();
              return true; // paste handled — stop ProseMirror default
            }
          }

          return false; // let ProseMirror handle the paste normally
        }
      },
      onUpdate: ({ editor }) => {
        emit("update:modelValue", editor.getHTML());
      }
    });

    watch(
      () => props.modelValue,
      (value) => {
        if (editor && value !== editor.getHTML()) {
          editor.commands.setContent(value || "", false);
        }
      }
    );

    onBeforeUnmount(() => {
      editor.destroy();
    });

    /* ============================================================
     * IMAGE
     * ============================================================ */
    function openImageModal() {
      const attrs = editor.getAttributes("image") || {};

      imageInitial.src    = attrs.src    || "";
      imageInitial.alt    = attrs.alt    || "";
      imageInitial.width  = attrs.width  || "";
      imageInitial.height = attrs.height || "";

      showImageModal.value = true;
    }

    function closeImageModal() {
      showImageModal.value = false;
    }

    function saveImage(payload) {
      editor.chain().focus().setImage(payload).run();
      closeImageModal();
    }

    function onImageUploaded(payload) {
      emit("image-uploaded", payload);
    }

    /* ============================================================
     * LINK
     * ============================================================ */
    function openLinkModal() {
      const attrs = editor.getAttributes("link") || {};

      const selection     = editor.state.selection;
      const selectedText  = editor.state.doc.textBetween(
        selection.from,
        selection.to,
        " "
      );

      linkInitial.href   = attrs.href   || "";
      linkInitial.target = attrs.target || "";
      linkInitial.title  = attrs.title  || "";
      linkInitial.text   = selectedText || "";

      showLinkModal.value = true;
    }

    function closeLinkModal() {
      showLinkModal.value = false;
    }

    function saveLink(payload) {
      const linkAttrs = { href: payload.href };

      if (payload.target) linkAttrs.target = payload.target;
      if (payload.title)  linkAttrs.title  = payload.title;

      const selection    = editor.state.selection;
      const hasSelection = selection.from !== selection.to;

      const originalSelectedText = hasSelection
        ? editor.state.doc.textBetween(selection.from, selection.to, " ")
        : "";

      const newText    = payload.text || payload.href;
      const textChanged = newText !== originalSelectedText;

      if (hasSelection && !textChanged) {
        // Text unchanged: only apply the link mark.
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .setLink(linkAttrs)
          .run();
      } else if (hasSelection && textChanged) {
        // Text changed: replace the selection with the new text + link mark.
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .deleteSelection()
          .insertContent({
            type: "text",
            text: newText,
            marks: [{ type: "link", attrs: linkAttrs }]
          })
          .run();
      } else {
        // No selection: insert new text with the link mark applied.
        editor
          .chain()
          .focus()
          .insertContent({
            type: "text",
            text: newText,
            marks: [{ type: "link", attrs: linkAttrs }]
          })
          .run();
      }

      closeLinkModal();
    }

    function removeLink() {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .unsetLink()
        .run();

      closeLinkModal();
    }

    function onLinkUploaded(payload) {
      emit("file-uploaded", payload);
    }

    /* ============================================================
     * SOURCE CODE
     * ============================================================ */
    function openSourceModal() {
      sourceHtml.value       = editor.getHTML();
      showSourceModal.value  = true;
    }

    function closeSourceModal() {
      showSourceModal.value = false;
    }

    function saveSource(html) {
      editor.commands.setContent(html || "", true, {
        preserveWhitespace: "full"
      });
      emit("update:modelValue", editor.getHTML());
      closeSourceModal();
    }

    /* ============================================================
     * TABLE
     * ============================================================ */
    function insertTable() {
      editor
        .chain()
        .focus()
        .insertTable({
          rows: 3,
          cols: 3,
          withHeaderRow: true
        })
        .run();
    }

    /* ============================================================
     * FULLSCREEN
     * ============================================================ */
    function toggleFullscreen() {
      fullscreen.value = !fullscreen.value;

      if (fullscreen.value) {
        document.body.classList.add("badiu-tiptap-fullscreen-lock");
      } else {
        document.body.classList.remove("badiu-tiptap-fullscreen-lock");
      }
    }

    /* ============================================================
     * PASTE — base64 images
     * ============================================================
     *
     * The handler lives in editorProps.handlePaste (see editor
     * instantiation above). That is the idiomatic Tiptap approach:
     * it goes through the ProseMirror pipeline instead of relying
     * on DOM events bubbling through the template.
     */

    /* ============================================================
     * ERRORS
     * ============================================================ */
    function onUploadError(payload) {
      emit("upload-error", payload);
    }

    return {
      // Editor
      editor,

      // Configuration
      baseUrl,

      // Modals
      showImageModal,
      showLinkModal,
      showSourceModal,
      imageInitial,
      linkInitial,
      sourceHtml,

      // UI state
      fullscreen,

      // Handlers
      openImageModal,
      closeImageModal,
      saveImage,
      onImageUploaded,

      openLinkModal,
      closeLinkModal,
      saveLink,
      removeLink,
      onLinkUploaded,

      openSourceModal,
      closeSourceModal,
      saveSource,

      insertTable,
      toggleFullscreen,

      onUploadError
    };
  }
};
</script>

<style>
/* ============================================================
 * Editor container — escopado para resistir a CSS do host (ex.:
 * Bootstrap dentro de um .row/.col que poderia herdar flex,
 * margens negativas, larguras etc.).
 * ============================================================ */
.badiu-tiptap-editor {
  position: relative;
  display: block;
  width: 100%;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  overflow: hidden;
  background: #fff;
  /* Reset de flex/grid herdado do host. */
  flex: none;
  margin: 0;
}

.badiu-tiptap-editor *,
.badiu-tiptap-editor *::before,
.badiu-tiptap-editor *::after {
  box-sizing: border-box;
}

/* ============================================================
 * Editable content area — usa flex-grow no modo fullscreen,
 * altura mínima sólida no modo normal para que o editor vazio
 * tenha o mesmo "porte" do TinyMCE.
 * ============================================================ */
.badiu-tiptap-editor .badiu-tiptap-content {
  display: block;
  padding: 14px 16px;
  min-height: 240px;
  background: #fff;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror {
  outline: none;
  min-height: 220px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror > * + * {
  margin-top: 0.75em;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #adb5bd;
  pointer-events: none;
  height: 0;
}

/* ============================================================
 * Image node
 * ============================================================ */
.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror img {
  max-width: 100%;
  height: auto;
  cursor: default;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror img.ProseMirror-selectednode {
  outline: 3px solid #0d6efd;
  outline-offset: 2px;
}

/* ============================================================
 * Table
 * ============================================================ */
.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror table {
  border-collapse: collapse;
  margin: 8px 0;
  table-layout: fixed;
  width: 100%;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror th,
.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror td {
  border: 1px solid #dee2e6;
  padding: 6px 10px;
  vertical-align: top;
  min-width: 40px;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror th {
  background: #f8f9fa;
  font-weight: 600;
}

/* ============================================================
 * Fullscreen
 * ============================================================ */
.badiu-tiptap-editor.badiu-tiptap-fullscreen {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  inset: 0;
  width: 100vw !important;
  height: 100vh !important;
  max-width: none !important;
  max-height: none !important;
  z-index: 99999;
  display: flex !important;
  flex-direction: column !important;
  background: #fff;
  border-radius: 0 !important;
  margin: 0 !important;
}

.badiu-tiptap-editor.badiu-tiptap-fullscreen .badiu-tiptap-content {
  flex: 1 1 auto;
  overflow: auto;
  min-height: 0;
}

body.badiu-tiptap-fullscreen-lock {
  overflow: hidden;
}

/* ============================================================
 * Status bar (footer)
 * ============================================================ */
.badiu-tiptap-editor .badiu-tiptap-statusbar {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  padding: 0.25rem 0.75rem !important;
  background: #f8f9fa !important;
  border-top: 1px solid #dee2e6 !important;
  font-size: 0.8rem;
  color: #6c757d;
}

.badiu-tiptap-editor .badiu-tiptap-counter {
  font-variant-numeric: tabular-nums;
  color: #6c757d;
}

.badiu-tiptap-editor .badiu-tiptap-brand {
  margin-left: auto;
  font-style: italic;
  color: #6c757d;
}
</style>
